// src/components/ReportQuizQuestionModal/ReportQuizQuestionModal.jsx
import React, { useState } from 'react';
import { reportQuizQuestion } from '../../services/feedbackService';
import styles from './ReportQuizQuestionModal.module.css';

const REPORT_REASONS = [
    { value: 'wrong_answer_marked_correct', label: 'Wrong answer marked correct' },
    { value: 'correct_answer_missing', label: 'Correct answer missing' },
    { value: 'multiple_correct_answers', label: 'More than one answer could be correct' },
    { value: 'factual_error', label: 'Question contains a factual error' },
    { value: 'question_confusing', label: 'Question is confusing' },
    { value: 'spoiler_issue', label: 'Question has a spoiler issue' },
    { value: 'wrong_media', label: 'Question does not match this movie/show/season' },
    { value: 'typo_wording', label: 'Typo or wording issue' },
    { value: 'other', label: 'Other' },
];

// Reasons offered when reporting from Review Missed Questions.
export const MISSED_REVIEW_REPORT_REASONS = [
    { value: 'wrong_answer_marked_correct', label: 'Correct answer seems wrong' },
    { value: 'question_confusing', label: 'Question is unclear' },
    { value: 'multiple_correct_answers', label: 'More than one answer could be correct' },
    { value: 'factual_error', label: 'Question contains a factual error' },
    { value: 'other', label: 'Other' },
];

function ReportQuizQuestionModal({
                                     isOpen,
                                     onClose,
                                     quizAttemptId,
                                     questionId,
                                     questionText,
                                     selectedAnswer,
                                     reasons = REPORT_REASONS,
                                     answerContext,
                                     metadata: extraMetadata,
                                     onSubmitted,
                                 }) {
    const [reason, setReason] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!reason) {
            setError('Please select a reason');
            return;
        }

        setLoading(true);

        try {
            const metadata = {
                ...(extraMetadata || {}),
                ...(selectedAnswer !== undefined && selectedAnswer !== null ? { selectedIndex: selectedAnswer } : {}),
            };
            const response = await reportQuizQuestion({
                quizAttemptId,
                questionId,
                reason,
                message: message.trim() || undefined,
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                metadata: Object.keys(metadata).length ? metadata : undefined,
            });

            setSuccessMessage(response?.duplicate
                ? 'You already reported this question — thanks, we\'ll review it.'
                : 'Thanks — we\'ll review this question.');
            setSuccess(true);
            onSubmitted?.({ reason, duplicate: response?.duplicate === true });

            setTimeout(() => {
                onClose();
                setReason('');
                setMessage('');
                setSuccess(false);
                setSuccessMessage('');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to submit report. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    function handleClose() {
        if (loading) return;
        setReason('');
        setMessage('');
        setError('');
        setSuccess(false);
        setSuccessMessage('');
        onClose();
    }

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="report-quiz-question-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="report-quiz-question-title">Report question</h2>
                    <button
                        className={styles.modalCloseBtn}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <p className={styles.modalDescription}>
                    Tell us what seems wrong with this question so we can improve our challenges.
                </p>

                {questionText && (
                    <div className={styles.questionPreview}>
                        <strong>Question:</strong> {questionText}
                        {answerContext && (
                            <dl className={styles.answerContext}>
                                <dt>Your answer</dt>
                                <dd>{answerContext.userAnswerText ?? 'No answer'}</dd>
                                <dt>Correct answer</dt>
                                <dd>{answerContext.correctAnswerText}</dd>
                            </dl>
                        )}
                    </div>
                )}

                {success && (
                    <div className={styles.modalSuccess} role="status">
                        ✓ {successMessage}
                    </div>
                )}

                {error && (
                    <div className={styles.modalError} role="alert">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    <div className={styles.formGroup}>
                        <label htmlFor="reason" className={styles.formLabel}>
                            What's wrong? <span className={styles.required}>*</span>
                        </label>
                        <select
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className={styles.formSelect}
                            disabled={loading || success}
                            required
                        >
                            <option value="">Select a reason...</option>
                            {reasons.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="message" className={styles.formLabel}>
                            What should we know?
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className={styles.formTextarea}
                            placeholder="Example: The answer marked correct doesn't seem right…"
                            rows={4}
                            maxLength={3000}
                            disabled={loading || success}
                        />
                    </div>

                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={handleClose}
                            disabled={loading || success}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.btnPrimary}
                            disabled={loading || success || !reason}
                        >
                            {loading ? 'Sending...' : 'Send Report'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ReportQuizQuestionModal;